import type { RefObject } from "react";
import { codeFromText } from "./paste";
import { tokens } from "./tokens";

const CARET_KEYFRAMES = "@keyframes lid-caret { 50% { opacity: 0; } }";

/**
 * The one-time code: six slots over one real input. The slots are drawn by
 * the host's tokens; the input underneath is transparent and does all the
 * work, so a code autofills in a single tap and the eye can count what has
 * been typed. A paste is read for its code, so the code copied with its
 * spacing or with the sentence around it fills the slots. Verification,
 * resend and the words around it are the host's.
 */
export function CodeSlots({
  value,
  onChange,
  id,
  inputRef,
  disabled = false,
  busy = false,
  dimWhenBusy = true,
  invalid = false,
  accepted = false,
  focused,
  onFocus,
  onBlur,
  length = 6,
  label = "Six-digit code",
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
  /** Verifying: the slots dim and take no input. The input keeps its focus,
   *  and the phone its keyboard, so a rejected code is retyped at once. */
  busy?: boolean;
  /** Off for a host that shows the wait itself: busy slots keep their
   *  contrast. */
  dimWhenBusy?: boolean;
  /** The last code was wrong: the slots turn the danger colour. */
  invalid?: boolean;
  /** The code was accepted: the slots turn the success colour. */
  accepted?: boolean;
  /** Whether the caret shows; the host tracks focus so it can style
   *  around the slots too. */
  focused: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  length?: number;
  label?: string;
}) {
  const active = focused && !busy ? Math.min(value.length, length - 1) : -1;
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <style>{CARET_KEYFRAMES}</style>
      <div aria-hidden="true" style={{ display: "flex", gap: tokens.space }}>
        {Array.from({ length }, (_, index) => {
          const isActive = index === active;
          return (
            <span
              key={index}
              style={{
                alignItems: "center",
                background: accepted
                  ? `color-mix(in srgb, ${tokens.success} 10%, ${tokens.surface})`
                  : tokens.surface,
                border: `${tokens.borderWidth} solid ${
                  invalid
                    ? tokens.danger
                    : accepted
                      ? tokens.success
                      : isActive
                        ? tokens.accent
                        : tokens.border
                }`,
                borderRadius: tokens.radius,
                boxShadow: isActive ? tokens.focusRing : "none",
                boxSizing: "border-box",
                color: tokens.text,
                display: "flex",
                flex: "1 1 0",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
                height: tokens.controlHeight,
                justifyContent: "center",
                maxWidth: "3.25rem",
                minWidth: 0,
                opacity: busy && dimWhenBusy && !accepted ? 0.6 : 1,
                transition:
                  "border-color 120ms, box-shadow 120ms, background-color 200ms",
              }}
            >
              {value[index] ??
                (isActive ? (
                  <span
                    style={{
                      animation: "lid-caret 1s steps(1) infinite",
                      background: "currentColor",
                      display: "inline-block",
                      height: "1.1em",
                      width: 1,
                    }}
                  />
                ) : (
                  ""
                ))}
            </span>
          );
        })}
      </div>
      <input
        ref={inputRef}
        id={id}
        aria-label={label}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={length}
        aria-invalid={invalid || undefined}
        aria-busy={busy || undefined}
        disabled={disabled}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(event) => {
          if (busy) return;
          onChange(event.target.value.replace(/\D/g, "").slice(0, length));
        }}
        // The browser would cut a paste to `maxLength` before `onChange` sees
        // it, so "663 970" would arrive as "663 97". Read the clipboard here.
        onPaste={(event) => {
          event.preventDefault();
          if (busy) return;
          const code = codeFromText(event.clipboardData.getData("text"), length);
          if (!code) return;
          // A whole code replaces what is there; part of one goes in at the
          // caret, as much of it as there is room for, as it would have
          // without this handler.
          const { selectionStart, selectionEnd } = event.currentTarget;
          const start = selectionStart ?? value.length;
          const end = selectionEnd ?? start;
          const room = length - value.length + (end - start);
          onChange(
            code.length === length
              ? code
              : value.slice(0, start) + code.slice(0, room) + value.slice(end),
          );
        }}
        style={{
          background: "transparent",
          border: 0,
          caretColor: "transparent",
          color: "transparent",
          fontSize: 16,
          height: "100%",
          inset: 0,
          letterSpacing: "-9999px",
          margin: 0,
          outline: 0,
          padding: 0,
          position: "absolute",
          width: "100%",
        }}
      />
    </div>
  );
}
